import type { TFunction } from 'i18next';
import { DocumentsRequestError } from '@apis/documents';

// Name rules the server enforces; the inline checks catch most before sending.
const NAME_CODES = new Set([
  'INVALID_FILE_NAME', 'INVALID_FOLDER_NAME', 'RESERVED_NAME',
  'FILE_NAME_TOO_LONG', 'FOLDER_NAME_TOO_LONG', 'FILE_EXTENSION_IMMUTABLE',
]);

interface IErrorMessageOptions {
  /** Covers anything unrecognised — a network failure, or a code with no message of its own. */
  fallbackKey?: string;
  /** The request carried a name (rename, new folder, upload). The store answers
   *  403 for a name it won't take as well as for a missing permission, so such a
   *  refusal names both; any other 403 can only be the permission. */
  sentName?: boolean;
}

/** A failed documents request in plain words. */
export function documentsErrorMessage(
  t: TFunction,
  err: unknown,
  { fallbackKey = 'documents.errors.generic', sentName = false }: IErrorMessageOptions = {},
): string {
  if (!(err instanceof DocumentsRequestError)) return t(fallbackKey);
  if (err.code === 'DOCUMENT_STORE_UNAVAILABLE') return t('documents.errors.unavailable');
  if (err.code === 'DOCUMENT_NAME_TAKEN') return t('documents.errors.nameTaken');
  if (err.code === 'FILE_TOO_LARGE') return t('documents.errors.tooLarge');
  if (err.code && NAME_CODES.has(err.code)) return t('documents.validation.invalidName');
  if (err.status === 403) return t(sentName ? 'documents.errors.notAllowed' : 'documents.errors.forbidden');
  if (err.status === 404) return t('documents.errors.notFound');
  if (err.status === 409) return t('documents.errors.conflict');
  return t(fallbackKey);
}
